/* eslint-disable */
import { useEffect, useState, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';
import { Chip, ChipGroup } from '@src/components/common/ChipGroup';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { useStore } from '@src/stores-v2/StoreContext';
import useAddMediaToTimeline from '@src/hooks/useAddMediaToTimeline';
import FileUpload from '../common/FileUpload';
import PanelUploadButton from '../common/PanelUploadButton';

import FileListItem from '../common/FileListItem';
import SpinningLoading from '../SpinningLoading';
import SearchInput from './SearchInput';
import { useEditor } from '@src/stores-v2';
enum VideoTab {
  story = 'Story',
  organization = 'Organization',
}

type Video = {
  id: string;
  title: string;
  src: string;
  duration: number | undefined;
  thumbnailUrl: string | undefined;
};

const VideoArtifacts = observer(() => {
  const videoCreator = useVideoCreatorStore();
  const { datoClientStore } = useStore();
  const editorStore = useEditor();
  const { addMediaToTimeline } = useAddMediaToTimeline({
    editorStore,
    videoCreator,
  });

  const [isFileUploading, setIsFileUploading] = useState(false);
  const [tab, setTab] = useState<VideoTab>(VideoTab.story);

  const storyVideos = useMemo(
    () =>
      videoCreator?.story?.storyArtifactsVideo?.map(a => ({
        id: a.id,
        title: a.title,
        src: a.url,
        duration: a.video?.duration,
        thumbnailUrl: a.video?.thumbnailUrl,
      })) || [],
    [videoCreator?.story?.storyArtifactsVideo],
  );

  const organizationVideos = useMemo(
    () =>
      (videoCreator.story?._allReferencingShowcases || []).flatMap(album =>
        (album.organizationArtifactsVideo || []).map(v => ({
          id: v.id,
          title: v.title,
          src: v.url,
          duration: v.video?.duration,
          thumbnailUrl: v.video?.thumbnailUrl,
        })),
      ) || [],
    [videoCreator.story?._allReferencingShowcases],
  );

  const [videos, setVideos] = useState<Video[] | undefined>(storyVideos);

  useEffect(() => {
    if (tab === VideoTab.story) {
      setVideos(storyVideos);
    } else {
      setVideos(organizationVideos);
    }
  }, [tab, storyVideos, organizationVideos]);

  const handleSearchArtifacts = (inputString: string) => {
    let resource;
    if (tab === VideoTab.story) {
      resource = videoCreator?.story?.storyArtifactsVideo
        ?.map(a => ({
          id: a.id,
          title: a.title,
          src: a.url,
          duration: a.video?.duration,
          thumbnailUrl: a.video?.thumbnailUrl,
        }))
        .filter(a => a.title.toLowerCase().includes(inputString.toLowerCase()));
    } else {
      resource = (videoCreator.story?._allReferencingShowcases || [])
        .flatMap(album =>
          (album.organizationArtifactsVideo || []).map(v => ({
            id: v.id,
            title: v.title,
            src: v.url,
            duration: v.video?.duration,
            thumbnailUrl: v.video?.thumbnailUrl,
          })),
        )
        .filter(a => a.title.toLowerCase().includes(inputString.toLowerCase()));
    }
    setVideos(resource);
  };

  async function handleRemoveVideo(mediaId: string) {
    if (tab === VideoTab.story) {
      const artifactVideos =
        videoCreator.story?.storyArtifactsVideo?.filter(
          artifact => artifact.id !== mediaId,
        ) || [];

      videoCreator.story!.storyArtifactsVideo = artifactVideos;
      setVideos(
        artifactVideos.map(v => ({
          id: v.id,
          title: v.title,
          src: v.url,
          duration: v.video?.duration,
          thumbnailUrl: v.video?.thumbnailUrl,
        })),
      );
      await videoCreator.updateStory(videoCreator.story!);
    } else {
      const albumIdx = videoCreator.story?._allReferencingShowcases?.findIndex(
        a => a.organizationArtifactsVideo?.some(v => v.id === mediaId),
      );
      if (albumIdx !== undefined && albumIdx > -1) {
        videoCreator.story!._allReferencingShowcases[
          albumIdx
        ].organizationArtifactsVideo =
          videoCreator.story!._allReferencingShowcases[
            albumIdx
          ].organizationArtifactsVideo?.filter(a => a.id !== mediaId) || [];

        setVideos(videos?.filter(v => v.id !== mediaId) || []);
        await datoClientStore.albumRepository?.update(
          videoCreator.story!._allReferencingShowcases[albumIdx]!,
        );
      }
    }
  }

  const handleSelectResource = async (resource: Video) => {
    const mediaType: 'video' | 'photo' | 'logo' = 'video';

    const options = {
      volume: 80,
      fit: 'cover' as const,
      autoFade: false,
    };

    await addMediaToTimeline(
      { ...resource, description: resource.title },
      mediaType,
      options,
    );
  };

  const renderTopActions = () => (
    <VideoTopAction>
      <SearchInput
        placeholder="Search Videos"
        handleAction={handleSearchArtifacts}
        radius="var(--radius-input)"
        iconRight
      />
      <FileUpload
        width="100%"
        Button={<PanelUploadButton>Upload Video</PanelUploadButton>}
        type={
          tab === VideoTab.story
            ? 'storyArtifactsVideo'
            : 'organizationArtifactsVideo'
        }
        callback={() => {
          let videoArtifactData;
          if (tab === VideoTab.story) {
            videoArtifactData = (videoCreator.story!.storyArtifactsVideo?.map(
              v => ({
                id: v.id,
                title: v.title,
                src: v.url,
                duration: v.video?.duration,
                thumbnailUrl: v.video?.thumbnailUrl,
              }),
            ) || []) as Video[];
          } else {
            videoArtifactData = (
              videoCreator.story?._allReferencingShowcases || []
            ).flatMap(album =>
              (album.organizationArtifactsVideo || []).map(v => ({
                id: v.id,
                title: v.title,
                src: v.url,
                duration: v.video?.duration,
                thumbnailUrl: v.video?.thumbnailUrl,
              })),
            );
          }
          setVideos(videoArtifactData);
        }}
        setIsLoading={setIsFileUploading}
      />
    </VideoTopAction>
  );

  return (
    <Main>
      {isFileUploading && <SpinningLoading text="Saving media…" />}

      <Tabs>
        {Object.entries(VideoTab).map(([k, v]) => (
          <Tab key={k} isSelected={tab === v} onClick={() => setTab(v)}>
            {v}
          </Tab>
        ))}
      </Tabs>
      {renderTopActions()}

      <VideoContent>
        {videos?.map(r => (
          <FileListItem
            key={r.id}
            type={
              tab === VideoTab.story
                ? 'storyArtifactsVideo'
                : 'organizationArtifactsVideo'
            }
            item={{
              id: r.id,
              description: r.title,
              src: r.src!,
              thumbnailUrl: r.thumbnailUrl,
            }}
            handleClick={() => handleSelectResource(r)}
            isSelected={false}
            handleRemove={() => handleRemoveVideo(r.id)}
          />
        ))}
      </VideoContent>
    </Main>
  );
});

export default VideoArtifacts;

const Main = styled.div`
  margin-top: 5px;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-gutter: stable both-edges;
  padding-left: var(--spacing-sm);
  padding-right: var(--spacing-sm);
  /* No bottom spacing here: the panel card's Body already contributes
     --spacing-md, which is the whole of what the Speakers panel uses and is
     the pattern every tab now follows. Adding more here double-counted it. */
  box-sizing: border-box;
`;

const VideoContent = styled.div`
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const VideoTopAction = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Tab = styled(Chip)``;
const Tabs = styled(ChipGroup)``;
